import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import {
  MessageCircle, ClipboardList, Lock, Send, Loader2, Clock,
  AlertCircle, CheckCircle2, Circle, ChevronRight, Plus,
  ArrowUpRight, Users, X
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INTERNAL_ROLES = new Set(["admin", "originations", "underwriter", "processor", "lender_relations", "investor_relations"]);

const PRIORITY_STYLES = {
  critical: "bg-red-100 text-red-700 border-red-200",
  high: "bg-orange-100 text-orange-700 border-orange-200",
  medium: "bg-amber-100 text-amber-700 border-amber-200",
  low: "bg-gray-100 text-gray-600 border-gray-200",
};

const STATUS_STYLES = {
  open: { icon: Circle, color: "text-blue-500", bg: "bg-blue-50 text-blue-700 border-blue-200" },
  in_progress: { icon: Loader2, color: "text-amber-500", bg: "bg-amber-50 text-amber-700 border-amber-200" },
  completed: { icon: CheckCircle2, color: "text-emerald-500", bg: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { icon: X, color: "text-red-500", bg: "bg-red-50 text-red-700 border-red-200" },
  cancelled: { icon: X, color: "text-gray-400", bg: "bg-gray-50 text-gray-500 border-gray-200" },
};

const THREAD_TYPE_LABELS = {
  borrower_internal: "Borrower \u2194 Internal",
  lender_internal: "Lender \u2194 Internal",
  investor_internal: "Investor \u2194 Capital Team",
  internal_only: "Internal Only",
};

const ROLE_COLORS = {
  admin: "bg-emerald-100 text-emerald-700",
  originations: "bg-teal-100 text-teal-700",
  underwriter: "bg-indigo-100 text-indigo-700",
  processor: "bg-cyan-100 text-cyan-700",
  lender_relations: "bg-violet-100 text-violet-700",
  investor_relations: "bg-fuchsia-100 text-fuchsia-700",
  borrower: "bg-blue-100 text-blue-700",
  lender: "bg-purple-100 text-purple-700",
  investor: "bg-amber-100 text-amber-700",
};

/* ═══════════════════════════════════════════════
   RequestCard — Single request/task display
   ═══════════════════════════════════════════════ */
function RequestCard({ request, onStatusChange }) {
  const statusDef = STATUS_STYLES[request.status] || STATUS_STYLES.open;
  const StatusIcon = statusDef.icon;
  return (
    <div className="rounded-lg border border-[#e8e0d0] bg-white p-3 hover:shadow-sm transition-shadow" data-testid={`request-card-${request.request_id}`}>
      <div className="flex items-start gap-2.5">
        <StatusIcon className={`h-4 w-4 mt-0.5 shrink-0 ${statusDef.color} ${request.status === "in_progress" ? "animate-spin" : ""}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-[#222] truncate">{request.title}</span>
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${PRIORITY_STYLES[request.priority] || PRIORITY_STYLES.medium}`}>
              {request.priority}
            </span>
          </div>
          {request.description && (
            <p className="text-xs text-[#666] mt-1 line-clamp-2">{request.description}</p>
          )}
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border ${statusDef.bg}`}>
              {request.status.replace(/_/g, " ")}
            </span>
            {request.assigned_to_name && (
              <span className="text-[10px] text-[#888] flex items-center gap-0.5">
                <Users className="h-2.5 w-2.5" /> {request.assigned_to_name}
              </span>
            )}
            {request.due_date && (
              <span className="text-[10px] text-[#888] flex items-center gap-0.5">
                <Clock className="h-2.5 w-2.5" /> Due {new Date(request.due_date).toLocaleDateString()}
              </span>
            )}
            <span className="text-[10px] text-[#bbb]">
              by {request.created_by_name || "—"} &middot; {request.created_at ? new Date(request.created_at).toLocaleDateString() : ""}
            </span>
          </div>
        </div>
        {onStatusChange && request.status === "open" && (
          <button onClick={() => onStatusChange(request.request_id, "in_progress")}
            className="text-[10px] px-2 py-1 rounded bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors shrink-0"
            data-testid={`request-start-${request.request_id}`}>
            Start
          </button>
        )}
        {onStatusChange && request.status === "in_progress" && (
          <button onClick={() => onStatusChange(request.request_id, "completed")}
            className="text-[10px] px-2 py-1 rounded bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors shrink-0"
            data-testid={`request-complete-${request.request_id}`}>
            Complete
          </button>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   CreateRequestForm — Create a new request/task
   ═══════════════════════════════════════════════ */
function CreateRequestForm({ entityType, entityId, onCreated, onCancel }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [priority, setPriority] = useState("medium");
  const [dueDate, setDueDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [users, setUsers] = useState([]);
  const [searchQ, setSearchQ] = useState("");
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (searchQ.length >= 1) {
      const t = setTimeout(() => {
        axios.get(`${API}/entity/users/mention-search?q=${encodeURIComponent(searchQ)}`, { headers })
          .then(r => setUsers(r.data.users || []))
          .catch(() => {});
      }, 250);
      return () => clearTimeout(t);
    }
  }, [searchQ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !assignedTo || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/comm/requests`, {
        title: title.trim(),
        description: description.trim(),
        assigned_to: assignedTo,
        entity_type: entityType,
        entity_id: entityId,
        priority,
        due_date: dueDate || null,
      }, { headers });
      onCreated();
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-emerald-200 bg-emerald-50/30 p-3 space-y-2" data-testid="create-request-form">
      <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Request title *"
        className="w-full px-2.5 py-1.5 rounded border border-[#e8e0d0] text-sm bg-white focus:border-emerald-400 outline-none"
        data-testid="request-title-input" />
      <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (optional)"
        rows={2} className="w-full px-2.5 py-1.5 rounded border border-[#e8e0d0] text-sm bg-white focus:border-emerald-400 outline-none resize-none"
        data-testid="request-description-input" />
      <div className="flex gap-2 flex-wrap">
        <div className="flex-1 min-w-[160px] relative">
          <input value={searchQ} onChange={e => { setSearchQ(e.target.value); setAssignedTo(""); }}
            placeholder="Assign to (search user) *"
            className="w-full px-2.5 py-1.5 rounded border border-[#e8e0d0] text-sm bg-white focus:border-emerald-400 outline-none"
            data-testid="request-assignee-input" />
          {users.length > 0 && !assignedTo && (
            <div className="absolute left-0 right-0 top-full mt-0.5 rounded border border-[#e8e0d0] bg-white shadow-lg z-20 max-h-32 overflow-y-auto">
              {users.map(u => (
                <button key={u.user_id} type="button" onClick={() => { setAssignedTo(u.user_id); setSearchQ(u.full_name); setUsers([]); }}
                  className="w-full flex items-center gap-2 px-2.5 py-1.5 text-left hover:bg-[#faf7f2] text-sm"
                  data-testid={`assignee-option-${u.user_id}`}>
                  <span className={`px-1 py-0.5 rounded text-[9px] font-bold ${ROLE_COLORS[u.role] || "bg-gray-100 text-gray-600"}`}>{u.role}</span>
                  <span className="text-[#333]">{u.full_name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <select value={priority} onChange={e => setPriority(e.target.value)}
          className="px-2 py-1.5 rounded border border-[#e8e0d0] text-sm bg-white"
          data-testid="request-priority-select">
          <option value="low">Low</option>
          <option value="medium">Medium</option>
          <option value="high">High</option>
          <option value="critical">Critical</option>
        </select>
        <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
          className="px-2 py-1.5 rounded border border-[#e8e0d0] text-sm bg-white"
          data-testid="request-due-date-input" />
      </div>
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded text-xs text-[#666] hover:bg-gray-100 transition-colors"
          data-testid="request-cancel-btn">Cancel</button>
        <button type="submit" disabled={!title.trim() || !assignedTo || submitting}
          className="px-3 py-1.5 rounded bg-emerald-700 text-white text-xs hover:bg-emerald-600 disabled:opacity-40 transition-colors"
          data-testid="request-submit-btn">
          {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Create Request"}
        </button>
      </div>
    </form>
  );
}

/* ═══════════════════════════════════════════════
   ThreadView — Message thread display + send
   ═══════════════════════════════════════════════ */
function ThreadView({ threadId, onBack }) {
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMsg, setNewMsg] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const { user: authUser } = useAuth();
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };
  const isInternal = authUser && INTERNAL_ROLES.has(authUser.role);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/comm/threads/${threadId}/messages`, { headers });
      setThread(res.data.thread);
      setMessages(res.data.messages || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [threadId]);

  useEffect(() => { fetchMessages(); }, [fetchMessages]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMsg.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/comm/threads/${threadId}/messages`, { content: newMsg.trim() }, { headers });
      setNewMsg("");
      fetchMessages();
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  const [forwardingMsg, setForwardingMsg] = useState(null);
  const [forwardTarget, setForwardTarget] = useState("");
  const [forwardNote, setForwardNote] = useState("");
  const [forwarding, setForwarding] = useState(false);

  const handleForward = async (msgId) => {
    if (!forwardTarget || forwarding) return;
    setForwarding(true);
    try {
      await axios.post(`${API}/comm/messages/${msgId}/forward`,
        { target_thread_type: forwardTarget, note: forwardNote.trim() || "" },
        { headers }
      );
      setForwardingMsg(null);
      setForwardTarget("");
      setForwardNote("");
      fetchMessages();
    } catch (err) { console.error(err); }
    finally { setForwarding(false); }
  };

  if (loading) return <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>;

  // Determine forward targets based on current thread type
  const currentType = thread?.thread_type || "";
  const forwardOptions = [];
  if (isInternal) {
    if (currentType === "borrower_internal") forwardOptions.push({ value: "lender_internal", label: "Forward to Lender" });
    if (currentType === "lender_internal") forwardOptions.push({ value: "borrower_internal", label: "Forward to Borrower" });
  }

  return (
    <div data-testid={`thread-view-${threadId}`}>
      <button onClick={onBack} className="text-xs text-emerald-600 hover:underline mb-2 flex items-center gap-1" data-testid="thread-back-btn">
        &larr; Back to threads
      </button>
      {thread && (
        <div className="mb-3">
          <h4 className="text-sm font-bold text-[#222]">{thread.subject || "Thread"}</h4>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#f0ebe0] text-[#666] font-bold">{THREAD_TYPE_LABELS[thread.thread_type] || thread.thread_type}</span>
            <span className="text-[10px] text-[#999]">{thread.message_count} messages</span>
          </div>
        </div>
      )}
      <div className="space-y-2 max-h-72 overflow-y-auto mb-3 pr-1">
        {messages.map(msg => {
          const isMe = msg.sender_id === authUser?.user_id;
          const msgExtra = msg.extra || {};
          const isForwarded = msgExtra.forwarded === true;
          const wasForwarded = msgExtra.forwarded_to;
          return (
            <div key={msg.message_id} className={`flex ${isMe ? "justify-end" : "justify-start"}`} data-testid={`message-${msg.message_id}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 ${isForwarded ? "bg-blue-50 border border-blue-200" : isMe ? "bg-emerald-50 border border-emerald-200" : "bg-[#faf7f2] border border-[#e8e0d0]"}`}>
                {isForwarded && (
                  <div className="flex items-center gap-1 mb-1">
                    <ArrowUpRight className="h-2.5 w-2.5 text-blue-500" />
                    <span className="text-[9px] text-blue-600 font-bold">FORWARDED</span>
                  </div>
                )}
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className={`text-[9px] px-1 py-0.5 rounded font-bold ${ROLE_COLORS[msg.sender_role] || "bg-gray-100 text-gray-600"}`}>
                    {msg.sender_name || "Unknown"}
                  </span>
                  <span className="text-[9px] text-[#bbb]">{msg.created_at ? new Date(msg.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}</span>
                  {wasForwarded && isInternal && (
                    <span className="text-[9px] text-emerald-600 italic">forwarded</span>
                  )}
                </div>
                <p className="text-xs text-[#333] whitespace-pre-wrap">{msg.content}</p>
                {/* Forward button — only for internal users on applicable threads */}
                {forwardOptions.length > 0 && !isForwarded && (
                  <div className="mt-1.5 pt-1.5 border-t border-[#e8e0d0]">
                    {forwardingMsg === msg.message_id ? (
                      <div className="space-y-1.5" data-testid={`forward-form-${msg.message_id}`}>
                        <select value={forwardTarget} onChange={e => setForwardTarget(e.target.value)}
                          className="w-full text-[10px] px-2 py-1 rounded border border-[#e8e0d0] bg-white"
                          data-testid="forward-target-select">
                          <option value="">Select target...</option>
                          {forwardOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                        <input value={forwardNote} onChange={e => setForwardNote(e.target.value)}
                          placeholder="Add a note (optional)..."
                          className="w-full text-[10px] px-2 py-1 rounded border border-[#e8e0d0]"
                          data-testid="forward-note-input" />
                        <div className="flex gap-1">
                          <button onClick={() => handleForward(msg.message_id)} disabled={!forwardTarget || forwarding}
                            className="text-[10px] px-2 py-0.5 rounded bg-emerald-700 text-white hover:bg-emerald-600 disabled:opacity-40"
                            data-testid="forward-confirm-btn">
                            {forwarding ? "Forwarding..." : "Forward"}
                          </button>
                          <button onClick={() => { setForwardingMsg(null); setForwardTarget(""); setForwardNote(""); }}
                            className="text-[10px] px-2 py-0.5 rounded border border-[#e8e0d0] text-[#666] hover:bg-[#faf7f2]"
                            data-testid="forward-cancel-btn">
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={() => setForwardingMsg(msg.message_id)}
                        className="text-[9px] text-emerald-600 hover:text-emerald-700 flex items-center gap-0.5"
                        data-testid={`forward-btn-${msg.message_id}`}>
                        <ArrowUpRight className="h-2.5 w-2.5" /> Forward
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      {thread?.status !== "closed" && (
        <form onSubmit={handleSend} className="flex gap-2" data-testid="thread-send-form">
          <input value={newMsg} onChange={e => setNewMsg(e.target.value)} placeholder="Type a message..."
            className="flex-1 px-3 py-2 rounded-lg border border-[#e8e0d0] text-sm bg-[#faf7f2] focus:border-emerald-400 outline-none"
            data-testid="thread-message-input" />
          <button type="submit" disabled={!newMsg.trim() || sending}
            className="px-3 py-2 rounded-lg bg-emerald-700 text-white hover:bg-emerald-600 disabled:opacity-40 transition-colors"
            data-testid="thread-send-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </form>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════
   CreateThreadForm — Create a new message thread
   ═══════════════════════════════════════════════ */
function CreateThreadForm({ entityType, entityId, userRole, onCreated, onCancel }) {
  const isInternal = INTERNAL_ROLES.has(userRole);
  const availableTypes = [];
  if (userRole === "borrower" || isInternal) availableTypes.push("borrower_internal");
  if (userRole === "lender" || isInternal) availableTypes.push("lender_internal");
  if (userRole === "investor" || isInternal) availableTypes.push("investor_internal");
  if (isInternal) availableTypes.push("internal_only");

  const [threadType, setThreadType] = useState(availableTypes[0] || "");
  const [subject, setSubject] = useState("");
  const [initialMessage, setInitialMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!threadType || !initialMessage.trim() || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/comm/threads`, {
        entity_type: entityType,
        entity_id: entityId,
        thread_type: threadType,
        subject: subject.trim() || initialMessage.trim().slice(0, 60),
        initial_message: initialMessage.trim(),
      }, { headers });
      onCreated();
    } catch (err) { console.error(err); }
    finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-emerald-200 bg-emerald-50/30 p-3 space-y-2" data-testid="create-thread-form">
      <select value={threadType} onChange={e => setThreadType(e.target.value)}
        className="w-full px-2.5 py-1.5 rounded border border-[#e8e0d0] text-sm bg-white"
        data-testid="thread-type-select">
        {availableTypes.map(t => <option key={t} value={t}>{THREAD_TYPE_LABELS[t]}</option>)}
      </select>
      <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject (optional)"
        className="w-full px-2.5 py-1.5 rounded border border-[#e8e0d0] text-sm bg-white focus:border-emerald-400 outline-none"
        data-testid="thread-subject-input" />
      <textarea value={initialMessage} onChange={e => setInitialMessage(e.target.value)} placeholder="Your message *"
        rows={2} className="w-full px-2.5 py-1.5 rounded border border-[#e8e0d0] text-sm bg-white focus:border-emerald-400 outline-none resize-none"
        data-testid="thread-message-input" />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 rounded text-xs text-[#666] hover:bg-gray-100 transition-colors"
          data-testid="thread-cancel-btn">Cancel</button>
        <button type="submit" disabled={!initialMessage.trim() || submitting}
          className="px-3 py-1.5 rounded bg-emerald-700 text-white text-xs hover:bg-emerald-600 disabled:opacity-40 transition-colors"
          data-testid="thread-submit-btn">
          {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Start Thread"}
        </button>
      </div>
    </form>
  );
}

/* ═══════════════════════════════════════════════
   EntityComms — Main communication panel
   ═══════════════════════════════════════════════ */
export default function EntityComms({ entityType, entityId, initialTab }) {
  const [comms, setComms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commTab, setCommTab] = useState(initialTab || "requests");
  const [showCreateRequest, setShowCreateRequest] = useState(false);
  const [showCreateThread, setShowCreateThread] = useState(false);
  const [activeThread, setActiveThread] = useState(null);
  const [internalNoteText, setInternalNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const { user: authUser } = useAuth();
  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };
  const isInternal = authUser && INTERNAL_ROLES.has(authUser.role);

  const fetchComms = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/comm/entity/${entityType}/${entityId}`, { headers });
      setComms(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [entityType, entityId]);

  useEffect(() => { fetchComms(); }, [fetchComms]);

  const handleStatusChange = async (requestId, newStatus) => {
    try {
      await axios.patch(`${API}/comm/requests/${requestId}`, { status: newStatus }, { headers });
      fetchComms();
    } catch (err) { console.error(err); }
  };

  const handleSaveInternalNote = async (e) => {
    e.preventDefault();
    if (!internalNoteText.trim() || savingNote) return;
    setSavingNote(true);
    try {
      await axios.post(`${API}/comm/internal-notes`, {
        entity_type: entityType,
        entity_id: entityId,
        content: internalNoteText.trim(),
      }, { headers });
      setInternalNoteText("");
      fetchComms();
    } catch (err) { console.error(err); }
    finally { setSavingNote(false); }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-emerald-600" /></div>;

  const counts = comms?.counts || {};
  const commTabs = [
    { key: "requests", label: "Requests", count: counts.requests_total || 0, icon: ClipboardList },
    { key: "threads", label: "Messages", count: counts.threads || 0, icon: MessageCircle },
    ...(isInternal ? [{ key: "internal_notes", label: "Internal Notes", count: counts.internal_notes || 0, icon: Lock }] : []),
  ];

  return (
    <div className="rounded-xl border border-[#e8e0d0] bg-white" data-testid="entity-comms">
      {/* Comm sub-tabs */}
      <div className="flex items-center gap-0.5 px-3 pt-3 pb-2 border-b border-[#e8e0d0] overflow-x-auto">
        {commTabs.map(ct => (
          <button key={ct.key} onClick={() => { setCommTab(ct.key); setActiveThread(null); }}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
              commTab === ct.key
                ? "bg-emerald-100 text-emerald-700"
                : "text-[#888] hover:text-[#333] hover:bg-[#faf7f2]"
            }`}
            data-testid={`comm-tab-${ct.key}`}>
            <ct.icon className="h-3.5 w-3.5" />
            {ct.label}
            {ct.count > 0 && (
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold ${
                commTab === ct.key ? "bg-emerald-200 text-emerald-800" : "bg-[#e8e0d0] text-[#666]"
              }`}>{ct.count}</span>
            )}
          </button>
        ))}
      </div>

      <div className="p-4">
        {/* ─── Requests/Tasks ─── */}
        {commTab === "requests" && (
          <div className="space-y-3" data-testid="comm-requests-panel">
            <div className="flex items-center justify-between">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#999]">
                Tasks & Requests ({counts.requests_open || 0} open)
              </h4>
              {isInternal && !showCreateRequest && (
                <button onClick={() => setShowCreateRequest(true)}
                  className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 hover:text-emerald-700 transition-colors"
                  data-testid="create-request-btn">
                  <Plus className="h-3 w-3" /> New Request
                </button>
              )}
            </div>
            <AnimatePresence>
              {showCreateRequest && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                  <CreateRequestForm entityType={entityType} entityId={entityId}
                    onCreated={() => { setShowCreateRequest(false); fetchComms(); }}
                    onCancel={() => setShowCreateRequest(false)} />
                </motion.div>
              )}
            </AnimatePresence>
            {(comms?.requests || []).length === 0 ? (
              <div className="text-center py-6 text-xs text-[#999]">
                <ClipboardList className="h-8 w-8 mx-auto text-[#ddd] mb-2" />
                No requests yet
              </div>
            ) : (
              <div className="space-y-2">
                {(comms?.requests || []).map(r => (
                  <motion.div key={r.request_id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}>
                    <RequestCard request={r} onStatusChange={handleStatusChange} />
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── Message Threads ─── */}
        {commTab === "threads" && (
          <div className="space-y-3" data-testid="comm-threads-panel">
            {activeThread ? (
              <ThreadView threadId={activeThread} onBack={() => setActiveThread(null)} />
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#999]">
                    Message Threads ({counts.threads || 0})
                  </h4>
                  {!showCreateThread && (
                    <button onClick={() => setShowCreateThread(true)}
                      className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 hover:text-emerald-700 transition-colors"
                      data-testid="create-thread-btn">
                      <Plus className="h-3 w-3" /> New Thread
                    </button>
                  )}
                </div>
                <AnimatePresence>
                  {showCreateThread && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}>
                      <CreateThreadForm entityType={entityType} entityId={entityId}
                        userRole={authUser?.role || ""}
                        onCreated={() => { setShowCreateThread(false); fetchComms(); }}
                        onCancel={() => setShowCreateThread(false)} />
                    </motion.div>
                  )}
                </AnimatePresence>
                {(comms?.threads || []).length === 0 ? (
                  <div className="text-center py-6 text-xs text-[#999]">
                    <MessageCircle className="h-8 w-8 mx-auto text-[#ddd] mb-2" />
                    No message threads yet
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(comms?.threads || []).map(t => (
                      <button key={t.thread_id} onClick={() => setActiveThread(t.thread_id)}
                        className="w-full text-left rounded-lg border border-[#e8e0d0] bg-white p-3 hover:shadow-sm hover:border-emerald-300 transition-all"
                        data-testid={`thread-item-${t.thread_id}`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 min-w-0">
                            <MessageCircle className="h-4 w-4 text-emerald-600 shrink-0" />
                            <span className="text-sm font-medium text-[#222] truncate">{t.subject || "Thread"}</span>
                          </div>
                          <ChevronRight className="h-3.5 w-3.5 text-[#ccc] shrink-0" />
                        </div>
                        <div className="flex items-center gap-2 mt-1.5 ml-6">
                          <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#f0ebe0] text-[#666] font-bold">
                            {THREAD_TYPE_LABELS[t.thread_type] || t.thread_type}
                          </span>
                          <span className="text-[10px] text-[#999]">{t.message_count} msgs</span>
                          {t.last_message_at && (
                            <span className="text-[10px] text-[#bbb]">
                              Last: {new Date(t.last_message_at).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ─── Internal Notes ─── */}
        {commTab === "internal_notes" && isInternal && (
          <div className="space-y-3" data-testid="comm-internal-notes-panel">
            <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#999] flex items-center gap-1.5">
              <Lock className="h-3 w-3" /> Internal Notes (Team Only)
            </h4>
            <form onSubmit={handleSaveInternalNote} className="flex gap-2" data-testid="internal-note-form">
              <input value={internalNoteText} onChange={e => setInternalNoteText(e.target.value)}
                placeholder="Add internal note (never visible to external users)..."
                className="flex-1 px-3 py-2 rounded-lg border border-[#e8e0d0] text-sm bg-[#faf7f2] focus:border-emerald-400 outline-none"
                data-testid="internal-note-input" />
              <button type="submit" disabled={!internalNoteText.trim() || savingNote}
                className="px-3 py-2 rounded-lg bg-emerald-700 text-white hover:bg-emerald-600 disabled:opacity-40 transition-colors"
                data-testid="internal-note-submit">
                {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              </button>
            </form>
            {(comms?.internal_notes || []).length === 0 ? (
              <div className="text-center py-6 text-xs text-[#999]">
                <Lock className="h-8 w-8 mx-auto text-[#ddd] mb-2" />
                No internal notes yet
              </div>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {(comms?.internal_notes || []).map(n => (
                  <motion.div key={n.note_id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                    className="rounded-lg bg-amber-50/50 border border-amber-200/50 p-3"
                    data-testid={`internal-note-${n.note_id}`}>
                    <p className="text-xs text-[#333]">{n.content}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${ROLE_COLORS[n.author_role] || "bg-gray-100 text-gray-600"}`}>
                        {n.author_name || "—"}
                      </span>
                      <span className="text-[9px] text-[#bbb] flex items-center gap-0.5">
                        <Clock className="h-2.5 w-2.5" />
                        {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
